import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2-old";
import ReactTooltip from "react-tooltip";
// import Tooltip from "../../components/Tooltip";

const colorLegend = {
  vix: ["#18ff01", "hsla(97, 66%, 57%, 0.05)"],
  move: ["#00c0ff", "hsla(188, 100%, 55%, 0.05)"],
  vxtlt: ["#ff5402", "hsla(24, 70%, 54%, 0.05)"],
  jpmvxygl: ["#ffd203", "hsla(50, 94%, 52%, 0.05)"],
  lf98oas: ["#d300ff", "hsla(288, 100%, 55%, 0.05)"],
  csi: ["#fe2400", "hsla(9, 100%, 50%, 0.05)"],
  vvix: ["#01ffa7", "hsla(145, 100%, 55%, 0.05)"],
  vxtlt: ["#97a5c9", "hsla(223, 32%, 69%, 0.05)"],
};

const CrossAssetChart = (props) => {
  const [chartLabel, setChartLabel] = useState([]);
  const [chartPrice, setChartPrice] = useState([]);
  const [handler, setHandler] = useState("");

  useEffect(() => {
    ReactTooltip.rebuild();
  }, []);

  //Detecting screen size change
  useEffect(() => {
    const handling = (e) => setHandler({ matches: e.matches });
    window
      .matchMedia("(max-width: 480px)")
      .addEventListener("change", handling);
  }, [handler]);
  //Takes an array of data relevant to the chart's label and price and updates state
  //so that only the desired days to display will be returned.
  const renderData = (chartData, daysToDisplay) => {
    const chartLabel = [];
    const chartPrice = [];
    chartData.map((obj, i) => {
      if (i > chartData.length - daysToDisplay - 1) {
        chartPrice.push(obj);
      }
      return obj;
    });
    setChartLabel(chartLabel);
    setChartPrice(chartPrice);
  };

  React.useEffect(() => {
    //Triggers when user changes into a different time frame and updates the chart accordingly
    //renderData(chartData, daysToDisplay) takes in the chart data, and number of most recent days you want to display the data
    const updateData = (chartData) => {
      if (props.period === "5D") renderData(chartData, 5);
      else if (props.period === "1M") renderData(chartData, 30);
      else if (props.period === "3M") renderData(chartData, 90);
      else renderData(chartData, 365);
    };

    updateData(props.data);

    const temp = [];
    const daysToDisplay =
      props.period === "5D"
        ? 5
        : props.period === "1M"
        ? 30
        : props.period === "3M"
        ? 90
        : 365;
    props.labels.map((obj, i) => {
      if (i > props.labels.length - daysToDisplay - 1)
        temp.push(
          obj.charAt(5) === "0"
            ? obj.substring(6) + "-" + obj.substring(0, 4)
            : obj.substring(5) + "-" + obj.substring(0, 4),
        ); //date formatting
      return obj;
    });
    setChartLabel(temp);
  }, [props.period, props.update, props.data, props.labels]);

  const data = () => {
    return {
      labels: chartLabel,
      datasets: [
        {
          data: chartPrice,
          fill: true,
          backgroundColor: colorLegend[props.ticker]?.[1],
          borderColor: colorLegend[props.ticker]?.[0],
          pointRadius: 0,
        },
      ],
    };
  };

  const options = {
    maintainAspectRatio: false,
    radius: 0,
    animation: {
      duration: 0,
    },
    tooltips: {
      mode: "index",
      intersect: false,
      custom: function (tooltipModel) {
        //The colors of square labels on the tooltip depends on the background color of the line.
        //that is problematic since the background of the trading range lines are mostly transparent, making the
        //squares color look washed. the code below overrides that square label color and make it more visible.
        const tooltip = tooltipModel.labelColors;
        if (tooltip)
          for (let i = 0; i < tooltipModel.labelColors.length; i++) {
            if (tooltip[i].borderColor !== "white") {
              tooltip[i].backgroundColor = tooltip[i].backgroundColor.replace(
                ".05",
                "1",
              );
              tooltip[i].borderColor = tooltip[i].borderColor.replace(
                ".5",
                "1",
              );
            }
          }
      },
      //Return a decimal number for certain charts.
      callbacks: {
        label: function (tooltipItem, data) {
          if (props.title !== "VVIX/VIX") return tooltipItem.yLabel.toFixed(2);
          else return tooltipItem.yLabel;
        },
      },
    },
    legend: { display: false },
    layout: {
      padding: {
        left: 50,
        right: 50,
        top: 10,
        bottom: 10,
      },
    },
    scales: {
      xAxes: [
        {
          gridLines: {
            display: false,
          },
          ticks: {
            fontFamily: "capsule",
            fontColor: "#bbb8b8",
            minRotation: !window.matchMedia("(max-width: 480px)").matches
              ? 0
              : 30,
            maxRotation: !window.matchMedia("(max-width: 480px)").matches
              ? 0
              : 30,
            // Set max ticks limit depending on the selected period.
            maxTicksLimit:
              props.period === "1Y"
                ? chartLabel.length / 30
                : props.period === "3M"
                ? chartLabel.length / 10
                : props.period === "1M"
                ? chartLabel.length / 5
                : 30,
            // Return month names when the period is 1 Year.
            callback: function (value, index, values) {
              const monthNames = [
                "Jan",
                "Feb",
                "Mar",
                "Apr",
                "May",
                "Jun",
                "Jul",
                "Aug",
                "Sept",
                "Oct",
                "Nov",
                "Dec",
              ];

              if (props.period === "1Y")
                return monthNames[parseInt(value.split("-")[0]) - 1];
              return value.substring(0, value.length - 5);
            },
          },
        },
      ],
      yAxes: [
        {
          gridLines: {
            display: true,
            color: "#1a1a1a",
          },
          ticks: {
            maxTicksLimit: 5,
            fontFamily: "capsule",
            fontColor: "#bbb8b8",
            callback: function (value, index, values) {
              if (props.title !== "VVIX/VIX")
                return value.toLocaleString("en-US");
              else return value;
            },
          },
        },
      ],
    },
  };

  return (
    <div className="line-chart">
      <div
        className="cross-asset-chart-title"
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginLeft: 35,
          marginRight: 40,
          // color: colorLegend[props.title][0],
          opacity: 0.75,
        }}
      >
        <div>{props.altTitle}</div>
        <div style={{ display: "flex", flexDirection: "row" }}>
          {props.title}
          {" | Current Value: "}
          {props.data[props.data.length - 1]?.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
          <div
            data-tip={props.info}
            className="info-icon"
            style={{
              marginLeft: 6,
              marginTop: 4,
              marginRight: 7,
              minWidth: 12,
            }}
          >
            i
          </div>
        </div>
      </div>

      <div style={{ height: "200px", display: "flex" }}>
        <Line data={data} options={options} />
      </div>
      {/* <Tooltip /> */}
    </div>
  );
};

export default CrossAssetChart;
